import {
  Button,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  Offscreen,
  Step,
  Stepper
} from '@deque/cauldron-react';
import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';

import useDidUpdate from '../hooks/useDidUpdate';
import styles from './StepperModal.css';

type StepStatus = 'complete' | 'current' | 'future';
export interface Step {
  heading: string;
  content: ReactElement;
  isValid?: () => boolean;
}

export interface StepperModalProps {
  heading: string;
  steps: Step[];
  onClose: () => void;
  onSubmit: () => Promise<void>;
  dialogOpen?: boolean;
  stepOverride?: number | null;
  toast?: ReactNode;
  nextDisabled?: boolean;
  isLoading?: boolean;
}

const StepperModal = ({
  heading,
  onSubmit,
  steps,
  onClose,
  stepOverride = null,
  dialogOpen = false,
  toast,
  nextDisabled = false,
  isLoading = false
}: StepperModalProps): ReactElement => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [currentStep, setCurrentStep] = useState(0);

  const getStepStatus = (stepNumber: number) =>
    currentStep === stepNumber
      ? 'current'
      : currentStep > stepNumber
        ? 'complete'
        : 'future';

  const getOffscreenText = (stepStatus: StepStatus) =>
    stepStatus === 'complete' ? (
      <Offscreen>{t('Completed step')}</Offscreen>
    ) : stepStatus === 'future' ? (
      <Offscreen>{t('Future step')}</Offscreen>
    ) : (
      <Offscreen>{t('Current step')}</Offscreen>
    );

  const incrementStep = () => setCurrentStep(s => s + 1);
  const onBackClick = () => setCurrentStep(s => s - 1);
  const onContinueClick = async () => {
    const isValid = steps[currentStep].isValid
      ? steps[currentStep].isValid?.()
      : true;

    if (isValid) {
      if (currentStep === steps.length - 1) {
        await onSubmit();
        return;
      }
      incrementStep();
    }
  };

  useDidUpdate(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  useEffect(() => {
    if (stepOverride !== null) {
      setCurrentStep(stepOverride);
    }
  }, [stepOverride]);

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  return (
    <Modal
      className={styles.stepperModal}
      aria-labelledby="stepper-modal-dialog-heading"
      show={dialogOpen}
      heading={<span id="stepper-modal-dialog-heading">{heading}</span>}
      onClose={onClose}
    >
      {toast}
      <Stepper>
        {steps.map((step: Step, i: number) => (
          <Step
            key={step.heading}
            status={getStepStatus(i)}
            aria-label={step.heading}
          >
            {step.heading} {getOffscreenText(getStepStatus(i))}
          </Step>
        ))}
      </Stepper>
      <ModalContent>
        {isLoading ? (
          <Loader label={t('Loading...')} ref={loaderRef} />
        ) : (
          <div className={styles.dialogMainContent}>
            <h3 ref={headingRef} tabIndex={-1}>
              {steps[currentStep].heading}
            </h3>
            {steps[currentStep].content}
          </div>
        )}
      </ModalContent>
      <ModalFooter className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={currentStep === 0 ? onClose : onBackClick}
          disabled={isLoading}
        >
          {currentStep === 0 ? t('Cancel') : t('Back')}
        </Button>
        <Button onClick={onContinueClick} disabled={nextDisabled || isLoading}>
          {currentStep === Object.keys(steps).length - 1
            ? t('Save')
            : t('Continue')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StepperModal;
